/**
 * Required Parts / Procedures section for PM Inspection Process.
 * Displays parts mapped to current service level via getPartsForServiceLevel.
 * Columns: Done (checkbox), Item, Qty, Manufacturer, Part #, Attach
 * Tracks completion state for each item.
 * Supports photo/note attachments per item (Task 9.7).
 *
 * Validates: Requirements 9.5, 9.6, 9.7
 */

import { useCallback, useMemo, useState } from 'react';
import { PMPartsAndProcedure } from '../../types/pmTypes';
import { Btn, BtnIconOnly, DataTable, IconClose } from '../ui';
import type { DataTableColumn } from '../ui';
import { ItemAttachment } from './PMInspectionProcess';

interface RequiredPartsSectionProps {
    parts: PMPartsAndProcedure[];
    /** Matrix qty per part (cascade union — not catalog defaults). */
    quantities?: Record<string, number>;
    completedItems: Record<string, boolean>;
    onToggle: (partId: string) => void;
    /** Mark every listed part done (`true`) or clear all (`false`). */
    onSetAllCompleted: (completed: boolean) => void;
    attachments: Record<string, ItemAttachment>;
    onAttachmentChange: (partId: string, attachment: ItemAttachment) => void;
    unitLabel?: string;
    serviceLevelLabel?: string;
}

export const RequiredPartsSection = ({
    parts,
    quantities,
    completedItems,
    onToggle,
    onSetAllCompleted,
    attachments,
    onAttachmentChange,
    unitLabel,
    serviceLevelLabel,
}: RequiredPartsSectionProps) => {
    const [expandedItem, setExpandedItem] = useState<string | null>(null);

    const allCompleted = parts.length > 0 && parts.every((part) => Boolean(completedItems[part.id]));
    const someCompleted = parts.some((part) => Boolean(completedItems[part.id]));

    const handlePhotoAdd = (partId: string, files: FileList | null) => {
        if (!files) return;
        const current = attachments[partId] || { photos: [], note: '' };
        onAttachmentChange(partId, {
            ...current,
            photos: [...current.photos, ...Array.from(files)],
        });
    };

    const handleNoteChange = (partId: string, note: string) => {
        const current = attachments[partId] || { photos: [], note: '' };
        onAttachmentChange(partId, { ...current, note });
    };

    const handleRemovePhoto = (partId: string, index: number) => {
        const current = attachments[partId] || { photos: [], note: '' };
        const updated = [...current.photos];
        updated.splice(index, 1);
        onAttachmentChange(partId, { ...current, photos: updated });
    };

    const qtyFor = useCallback(
        (part: PMPartsAndProcedure) =>
            quantities?.[part.id] != null && quantities[part.id] > 0
                ? quantities[part.id]
                : (part.quantity ?? '—'),
        [quantities],
    );

    const columns = useMemo((): DataTableColumn<PMPartsAndProcedure>[] => [
        {
            id: 'done',
            header: (
                <span className="pm-inspection-done-heading">
                    <input
                        type="checkbox"
                        className="pm-inspection-checkbox"
                        checked={allCompleted}
                        ref={(el) => {
                            if (el) el.indeterminate = someCompleted && !allCompleted;
                        }}
                        onChange={() => onSetAllCompleted(!allCompleted)}
                        aria-label={allCompleted ? 'Unselect all required items' : 'Select all required items'}
                        title={allCompleted ? 'Unselect all' : 'Select all'}
                    />
                    <span aria-hidden="true">Done</span>
                </span>
            ),
            headerLabel: 'Done',
            thClassName: 'pm-inspection-col-done',
            tdClassName: 'pm-inspection-col-done',
            mobileCardStacked: true,
            renderCell: (part) => (
                <input
                    type="checkbox"
                    className="pm-inspection-checkbox"
                    checked={!!completedItems[part.id]}
                    onChange={() => onToggle(part.id)}
                    aria-label={`Mark ${part.itemName} as completed`}
                />
            ),
        },
        {
            id: 'item',
            header: 'Item',
            tdClassName: 'pm-inspection-item-name',
            mobileCardPrimary: true,
            renderCell: (part) => part.itemName,
        },
        {
            id: 'qty',
            header: 'Qty',
            thClassName: 'pm-inspection-col-qty',
            tdClassName: 'pm-inspection-col-qty',
            renderCell: (part) => qtyFor(part),
        },
        {
            id: 'manufacturer',
            header: 'Manufacturer',
            renderCell: (part) => part.manufacturer ?? '—',
        },
        {
            id: 'partNumber',
            header: 'Part #',
            renderCell: (part) => part.partNumber ?? '—',
        },
        {
            id: 'actions',
            header: 'Attach',
            headerLabel: 'Attach',
            thClassName: 'pm-inspection-col-attach',
            tdClassName: 'pm-inspection-col-attach',
            mobileCardAction: true,
            renderCell: (part) => {
                const att = attachments[part.id];
                const isExpanded = expandedItem === part.id;
                return (
                    <Btn
                        variant="link"
                        buttonSize="sm"
                        buttonText={
                            (att?.photos?.length ?? 0) > 0
                                ? `Attach (${att.photos.length})`
                                : 'Attach'
                        }
                        ariaLabel={`Add photo or note for ${part.itemName}`}
                        buttonOnClick={() =>
                            setExpandedItem(isExpanded ? null : part.id)
                        }
                    />
                );
            },
        },
    ], [
        allCompleted,
        attachments,
        completedItems,
        expandedItem,
        onSetAllCompleted,
        onToggle,
        qtyFor,
        someCompleted,
    ]);

    if (parts.length === 0) {
        return (
            <div className="pm-inspection-section pm-no-print">
                <h3 className="pm-inspection-section-title">Required Parts / Procedures</h3>
                <p className="pm-inspection-empty">No parts mapped to this service level.</p>
            </div>
        );
    }

    return (
        <>
        <div className="pm-inspection-section pm-no-print">
            <div className="pm-inspection-section-heading">
                <h3 className="pm-inspection-section-title">Required Parts / Procedures</h3>
                <Btn
                    variant="secondary-outlined"
                    buttonSize="sm"
                    buttonText="Print to-do list"
                    ariaLabel="Print the to-do list with a check spot for each item"
                    buttonOnClick={() => window.print()}
                />
            </div>
            <p className="pm-inspection-hint">
                Print this list for the shop. Tab through the boxes to mark what was finished on the paper form,
                or check items directly on the phone. Use the checkbox next to Done in the column header (or Select
                all / Deselect all on a phone) to mark every item. Unchecked consumables are not charged to Cost
                Tracking. At least one item must be marked done before submit.
            </p>
            <div className="pm-inspection-select-all-bar">
                <Btn
                    variant="link"
                    buttonSize="sm"
                    buttonText={allCompleted ? 'Deselect all' : 'Select all'}
                    ariaLabel={allCompleted ? 'Deselect all required items' : 'Select all required items'}
                    buttonOnClick={() => onSetAllCompleted(!allCompleted)}
                />
            </div>
            <DataTable
                caption="Required Parts and Procedures"
                captionVisuallyHidden
                tableClassName="pm-inspection-table"
                className="pm-inspection-table-wrap"
                columns={columns}
                rows={parts}
                getRowKey={(part) => part.id}
                mobileCardLayout
                corners="square"
                emptyState={<p className="pm-inspection-empty">No parts mapped to this service level.</p>}
            />

            {/* Expanded attachment panel (rendered below table for the selected item) */}
            {expandedItem && (
                <div className="pm-inspection-attachment-panel">
                    <div className="pm-inspection-attachment-header">
                        <span>
                            Attachments for:{' '}
                            <strong>
                                {parts.find((p) => p.id === expandedItem)?.itemName}
                            </strong>
                        </span>
                        <BtnIconOnly
                            size="sm"
                            icon={<IconClose />}
                            ariaLabel="Close attachments"
                            onClick={() => setExpandedItem(null)}
                        />
                    </div>

                    <div className="pm-inspection-attachment-body">
                        <div className="pm-inspection-field">
                            <label className="pm-inspection-label">Photos</label>
                            <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={(e) =>
                                    handlePhotoAdd(expandedItem, e.target.files)
                                }
                            />
                            {(attachments[expandedItem]?.photos?.length ?? 0) > 0 && (
                                <div className="pm-inspection-photo-list">
                                    {attachments[expandedItem].photos.map((f, i) => (
                                        <div key={i} className="pm-inspection-photo-item">
                                            <span>{f.name}</span>
                                            <BtnIconOnly
                                                size="sm"
                                                variant="danger"
                                                icon={<IconClose />}
                                                ariaLabel={`Remove ${f.name}`}
                                                onClick={() => handleRemovePhoto(expandedItem, i)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="pm-inspection-field">
                            <label className="pm-inspection-label">Note</label>
                            <textarea
                                className="pm-inspection-textarea"
                                rows={3}
                                placeholder="Add a note for this item…"
                                value={attachments[expandedItem]?.note ?? ''}
                                onChange={(e) =>
                                    handleNoteChange(expandedItem, e.target.value)
                                }
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
        <div className="pm-todo-print">
            <h1>PM to-do</h1>
            <p>{unitLabel || 'Unit'}</p>
            <p>Service level: {serviceLevelLabel || '—'}</p>
            <p>Check each line when the work is finished. Those marks can be entered in the app later.</p>
            <table>
                <thead>
                    <tr>
                        <th>Done</th>
                        <th>Item</th>
                        <th>Qty</th>
                        <th>Manufacturer</th>
                        <th>Part #</th>
                    </tr>
                </thead>
                <tbody>
                    {parts.map((part) => (
                        <tr key={part.id}>
                            <td><span className="pm-todo-print-box" aria-hidden="true" /></td>
                            <td>
                                {part.itemName}
                                {part.itemType?.toLowerCase() === 'procedure' ? ' (Procedure)' : ''}
                            </td>
                            <td>{qtyFor(part)}</td>
                            <td>{part.manufacturer ?? '—'}</td>
                            <td>{part.partNumber ?? '—'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
        </>
    );
};
